import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Siren, FirstAid, Fire, ShieldWarning, DotsThreeCircle, X, Check } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialog";

const TYPES = [
  { code: "MEDICAL", label: "Medical", Icon: FirstAid },
  { code: "FIRE", label: "Fire", Icon: Fire },
  { code: "SECURITY", label: "Security", Icon: ShieldWarning },
  { code: "OTHER", label: "Other", Icon: DotsThreeCircle },
];
const fmt = (d) => { try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

export default function Sos() {
  const { user } = useAuth();
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN", "SECURITY"].includes(user?.role);
  return isStaff ? <StaffSos /> : <ResidentSos />;
}

function ResidentSos() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/api/sos").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);
  return (
    <div className="space-y-5 font-sans" data-testid="sos-resident">
      <div><p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Emergency</p><h1 className="font-display text-3xl font-extrabold text-white">SOS</h1></div>
      <button onClick={() => setOpen(true)} data-testid="sos-trigger"
        className="flex w-full flex-col items-center gap-3 rounded-3xl border border-neon-pink/40 bg-neon-pink/10 py-10 text-neon-pink shadow-glow-pink active:scale-[0.98]">
        <Siren size={56} weight="fill" />
        <span className="font-display text-xl font-extrabold">Raise an SOS</span>
        <span className="text-[12px] text-neon-pink/70">Alerts security &amp; the society office instantly</span>
      </button>
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Your recent alerts</p>
        {items.length === 0 ? <div className="card p-5 text-center text-sm text-white/45">No alerts raised.</div>
          : items.map((s) => <SosRow key={s.id} s={s} />)}
      </div>
      <AnimatePresence>{open && <SosSheet onClose={() => setOpen(false)} onRaised={() => { setOpen(false); load(); }} />}</AnimatePresence>
    </div>
  );
}

function SosRow({ s, onResolve, staff }) {
  const T = TYPES.find((t) => t.code === s.type) || TYPES[3];
  const active = s.status === "ACTIVE";
  return (
    <div className={`card flex items-center gap-3 p-3.5 ${active ? "border-neon-pink/40" : ""}`} data-testid={`sos-${s.id}`}>
      <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${active ? "bg-neon-pink/15 text-neon-pink" : "bg-white/5 text-white/50"}`}><T.Icon size={20} weight="fill" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[14px] font-semibold text-white">{T.label}{staff && s.flatLabel ? ` · ${s.flatLabel}` : ""} <span className={`ml-1 text-[10px] font-bold ${active ? "text-neon-pink" : "text-neon-lime"}`}>{active ? "ACTIVE" : "RESOLVED"}</span></p>
        <p className="truncate text-[12px] text-white/55">{staff ? `${s.raisedByName} · ` : ""}{fmt(s.createdAt)}{s.note ? ` · ${s.note}` : ""}{s.status === "RESOLVED" && s.resolvedByName ? ` · by ${s.resolvedByName}` : ""}</p>
      </div>
      {staff && active && <button onClick={onResolve} className="rounded-xl bg-neon-lime px-3 py-1.5 text-[12px] font-bold text-black">Resolve</button>}
    </div>
  );
}

function SosSheet({ onClose, onRaised }) {
  const [type, setType] = useState("MEDICAL");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  async function raise() {
    setBusy(true);
    try { await api.post("/api/sos", { type, note: note || null }); onRaised(); } catch { setBusy(false); }
  }
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="sos-sheet">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl font-bold text-white">Raise SOS</h3><button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button></div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {TYPES.map((t) => { const on = type === t.code; return (
            <button key={t.code} onClick={() => setType(t.code)} className={`flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-4 ${on ? "border-neon-pink/60 bg-neon-pink/10 text-neon-pink" : "border-white/10 bg-white/[0.03] text-white/65"}`}>
              <t.Icon size={26} weight={on ? "fill" : "duotone"} /><span className="text-[12px] font-bold">{t.label}</span>
            </button>); })}
        </div>
        <input className="field mt-3" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a detail (optional)" />
        <button disabled={busy} onClick={raise} data-testid="sos-confirm" className="btn mt-4 w-full border border-neon-pink/40 bg-neon-pink text-black shadow-glow-pink disabled:opacity-50">
          {busy ? "Alerting…" : <><Siren size={16} weight="fill" className="mr-2" /> Send alert now</>}
        </button>
      </motion.div>
    </motion.div>
  );
}

function StaffSos() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get("/api/sos").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);
  async function resolve(s) { await api.post(`/api/sos/${s.id}/resolve`); load(); }
  const active = items.filter((s) => s.status === "ACTIVE");
  const past = items.filter((s) => s.status !== "ACTIVE");
  return (
    <div className="space-y-5 font-sans" data-testid="sos-staff">
      <div><p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Emergency</p><h1 className="font-display text-3xl font-extrabold text-white">SOS alerts</h1></div>
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-neon-pink">Active ({active.length})</p>
        {active.length === 0 ? <div className="card p-5 text-center text-sm text-white/45">No active alerts. All clear.</div>
          : active.map((s) => <SosRow key={s.id} s={s} staff onResolve={() => resolve(s)} />)}
      </div>
      {past.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Resolved (24h)</p>
          {past.map((s) => <SosRow key={s.id} s={s} staff />)}
        </div>
      )}
    </div>
  );
}
